import { MAX_CODE_POINTS } from '../pack/constants';

/**
 * Normalization, and the cheap bounds that keep it affordable.
 *
 * Classification runs synchronously on every input change with no debounce, so nothing
 * here may be proportional to what a user can paste. Two properties enforce that:
 *
 * - **Work is bounded before it starts.** `normalizeQuery` truncates to the widest input
 *   that could still be inside the code-point bound, so NFKC never runs over a megabyte
 *   of pasted text.
 * - **Nothing allocates per code point.** The bound check is a capped scan with an O(1)
 *   fast path, and the script and terminal-punctuation checks are single native passes.
 */

/**
 * The widest slice that can still hold a within-bound input.
 *
 * A code point is at most two UTF-16 code units, so any input of `MAX_CODE_POINTS` code
 * points fits in twice that many units. Anything longer is over the bound by definition,
 * and `S-INPUT-LIMIT` only needs to know *that* — so truncating here cannot change the
 * classification of any input the engine is willing to accept.
 */
const MAX_CODE_UNITS = MAX_CODE_POINTS * 2 + 2;

/**
 * Produces `qMatch`: the only string rules are allowed to match against.
 *
 * NFKC folds compatibility forms — notably the full-width question mark `？` to `?` — so
 * the terminal-punctuation tests in `C-AUX-QUESTION` and `C-TERMINAL-QUESTION` behave
 * identically for ASCII and full-width input. Internal Unicode whitespace collapses to a
 * single space so cue phrases match regardless of how the user spaced them.
 *
 * Display and submission text are never derived from this value. The caller keeps the raw
 * input, so normalization can never change what the user sees or sends.
 */
export const normalizeQuery = (input: string): string =>
	(input.length > MAX_CODE_UNITS ? input.slice(0, MAX_CODE_UNITS) : input)
		.normalize('NFKC')
		.replace(/\s+/gu, ' ')
		.trim();

/**
 * Returns whether `qMatch` holds more than `max` Unicode code points.
 *
 * A capped scan rather than a count: the answer is a boolean, so there is no reason to
 * walk past the bound, and no reason to allocate an array of every code point to get
 * there. UTF-16 length is always at least the code-point count, which gives an O(1) fast
 * path for every ordinary query.
 */
export const exceedsCodePoints = (qMatch: string, max: number = MAX_CODE_POINTS): boolean => {
	if (qMatch.length <= max) {
		return false;
	}

	let count = 0;

	for (const _codePoint of qMatch) {
		count += 1;

		if (count > max) {
			return true;
		}
	}

	return false;
};

/**
 * Letters outside the Latin script.
 *
 * The class negates the union of "non-letters" and "Latin", which leaves exactly the
 * letters that are not Latin — one native regex pass, rather than a per-code-point loop
 * that allocates a string for every character.
 */
const NON_LATIN_LETTER = /[^\P{L}\p{Script=Latin}]/u;

/**
 * Returns whether any letter in the input is outside the Latin script.
 *
 * The reviewed question grammars are English-only, so a non-Latin letter makes them
 * non-applicable. Digits, punctuation, and emoji are not letters and so do not trigger it.
 */
export const hasNonLatinLetter = (qMatch: string): boolean => NON_LATIN_LETTER.test(qMatch);

const TERMINAL_PUNCTUATION = /\p{P}|\p{S}/u;
const LOW_SURROGATE_START = 0xdc00;
const LOW_SURROGATE_END = 0xdfff;

/**
 * Returns the terminal punctuation code point of `qMatch`, or `''` if it ends otherwise.
 *
 * Reads the last code point directly instead of spreading the string into an array, which
 * for a 4,096-character paste would allocate 4,096 strings to look at one of them.
 */
export const getTerminalPunctuation = (qMatch: string): string => {
	if (qMatch.length === 0) {
		return '';
	}

	const lastUnit = qMatch.charCodeAt(qMatch.length - 1);
	const isLowSurrogate = lastUnit >= LOW_SURROGATE_START && lastUnit <= LOW_SURROGATE_END;
	const last = qMatch.slice(isLowSurrogate && qMatch.length >= 2 ? -2 : -1);

	return TERMINAL_PUNCTUATION.test(last) ? last : '';
};
